import { useMemo, useState } from 'react';
import { Calendar as CalendarIcon, ChevronDown } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { Calendar } from './ui/calendar';

/** Format a Date → "YYYY-MM-DD" (local time, avoids UTC drift) */
function fmt(d) {
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
/** Parse "YYYY-MM-DD" → local Date (avoids UTC parsing quirk of `new Date('2000-01-01')`) */
function parse(s) {
  if (!s || typeof s !== 'string') return undefined;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return undefined;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime()) ? undefined : d;
}
function labelFor(d, locale) {
  if (!d) return '';
  try {
    return d.toLocaleDateString(locale === 'ja' ? 'ja-JP' : 'en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return fmt(d);
  }
}

/**
 * Modern date picker built on shadcn Popover + Calendar (react-day-picker).
 * Uses ISO string ("YYYY-MM-DD") as its wire format so it drops in wherever
 * a native `<input type="date">` was used.
 *
 * Props:
 *   value       "YYYY-MM-DD" or ''
 *   onChange    (str) => void — receives an ISO string ('' when cleared)
 *   placeholder    text shown when empty
 *   testId         data-testid for the trigger button
 *   theme          'orange' | 'red' | 'neutral' (border/hover accent)
 *   dobMode        when true, defaults calendar to a sensible birth-year and enables year captions
 *   locale         'en' | 'ja' — used for the display label
 *   maxDate        Date — disables selection after this date (default: today for DOB mode, none otherwise)
 */
export default function DatePicker({
  value,
  onChange,
  placeholder = 'Pick a date',
  testId = 'date-picker',
  theme = 'neutral',
  dobMode = false,
  locale = 'en',
  maxDate,
}) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => parse(value), [value]);

  // Border accents matched to escape-room (orange) / haunted-house (red) themes
  const borderCls = {
    orange: 'border-neutral-700 hover:border-orange-500 focus:border-orange-500',
    red: 'border-neutral-700 hover:border-red-500 focus:border-red-500',
    neutral: 'border-neutral-700 hover:border-neutral-500 focus:border-neutral-500',
  }[theme] || '';
  const iconCls = {
    orange: 'text-orange-400',
    red: 'text-red-400',
    neutral: 'text-neutral-400',
  }[theme] || '';

  // For DOB mode, default calendar to 25 years ago so the year navigator lands
  // near the typical birth year; also cap the max date at today.
  const defaultMonth = useMemo(() => {
    if (selected) return selected;
    if (!dobMode) return undefined;
    const d = new Date();
    d.setFullYear(d.getFullYear() - 25);
    return d;
  }, [selected, dobMode]);
  const finalMax = maxDate || (dobMode ? new Date() : undefined);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={`w-full bg-black text-white border rounded-lg px-3 py-2 text-sm text-left flex items-center gap-2 outline-none transition-colors ${borderCls}`}
        >
          <CalendarIcon size={14} className={iconCls} />
          <span className={selected ? 'font-mono' : 'text-neutral-500'}>
            {selected ? labelFor(selected, locale) : placeholder}
          </span>
          <ChevronDown size={14} className="ml-auto text-neutral-500" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 w-auto bg-neutral-950 border-2 border-neutral-800"
        data-testid={`${testId}-popover`}
      >
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={defaultMonth}
          onSelect={(d) => {
            onChange(d ? fmt(d) : '');
            setOpen(false);
          }}
          captionLayout={dobMode ? 'dropdown-buttons' : undefined}
          fromYear={dobMode ? 1920 : undefined}
          toYear={dobMode ? new Date().getFullYear() : undefined}
          disabled={finalMax ? { after: finalMax } : undefined}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
